import { Link } from 'react-router-dom';
import SeoHelmet from '@/components/feature/SeoHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, aboutFaq, siteStats, hotTags } from '@/mocks/data';

export default function About() {
  const heroImage =
    'https://readdy.ai/api/search-image?query=Dark%20moody%20cityscape%20at%20night%20with%20dramatic%20red%20neon%20lights%20reflecting%20on%20wet%20streets%2C%20cinematic%20atmosphere%2C%20journalism%20and%20truth%20theme%2C%20high%20contrast%20black%20and%20red%20palette%2C%20editorial%20photography%20style&width=1920&height=1080&seq=hero-about&orientation=landscape';

  const faqSchemas = aboutFaq.map((item, index) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  }));

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `关于我们 - ${siteInfo.name}`,
      description: siteInfo.description,
      url: `${siteInfo.url}/about`,
      isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: siteInfo.url },
      about: { '@type': 'Organization', name: siteInfo.name },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqSchemas,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
      logo: siteInfo.logo,
      description: siteInfo.description,
      sameAs: [siteInfo.weibo, siteInfo.douyin, siteInfo.xiaohongshu].filter(Boolean),
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHelmet
        title={`关于我们 - ${siteInfo.name}`}
        description={`${siteInfo.name}品牌介绍、口碑信誉与联系方式。${siteInfo.description}`}
        keywords={`91吃瓜网,关于91吃瓜,吃瓜网站介绍,91吃瓜爆料平台,91吃瓜联系方式`}
        canonicalPath="/about"
        schemas={schemas}
      />
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative min-h-[70dvh] md:min-h-[80dvh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="关于91吃瓜网"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 pt-32">
          <h1 className="text-white font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-8 md:mb-12 max-w-3xl">
            关于91
            <br />
            吃瓜网络
            <br />
            真相至上
          </h1>

          <div className="flex flex-col md:flex-row gap-6 md:gap-12 max-w-4xl">
            <div className="md:w-1/2">
              <p className="text-white/80 text-sm md:text-base leading-relaxed">
                91吃瓜网创立于2023年，是国内领先的娱乐爆料资讯平台。我们以"还原真相、娱乐有道"为理念，
                致力于打破信息不对称，让每一位普通网友都能获取到娱乐圈与社会热点背后的真实故事。
              </p>
            </div>
            <div className="md:w-1/2">
              <p className="text-white/80 text-sm md:text-base leading-relaxed">
                我们坚信：吃瓜不是低俗，了解真相是每个人的权利。91吃瓜网坚持独立调查、多方验证，
                用专业的态度做不专业领域里最靠谱的爆料人。不打烊、不站队、不造谣。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT MODULE ===== */}
      <section className="bg-background-100 py-12 md:py-20">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="lg:w-1/2">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20investigative%20team%20meeting%20around%20conference%20table%20with%20laptops%20and%20documents%2C%20dramatic%20side%20lighting%2C%20dark%20modern%20office%2C%20red%20accent%20lighting%2C%20cinematic%20photography%20style%2C%20high%20contrast&width=800&height=500&seq=about-brand&orientation=landscape"
                  alt="91吃瓜网团队"
                  className="w-full h-[280px] md:h-[360px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:w-1/2">
              <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-4">
                关于我们
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                还原真相
                <br />
                <span className="text-primary-500">娱乐有道</span>
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  <strong className="text-foreground-950">品牌介绍：</strong>
                  91吃瓜网是一个专注于娱乐爆料与社会热点追踪的中文资讯平台。我们覆盖明星黑料、网红翻车、社会奇闻、国产综艺内幕等多个领域，日均更新爆料100+条，覆盖微博、抖音、小红书等50+平台渠道。
                </p>
                <p>
                  <strong className="text-foreground-950">口碑信誉：</strong>
                  自成立以来，91吃瓜网累计服务超过200万忠实瓜农用户。我们的独家爆料准确率高达92%，远高于行业平均水平。多篇深度调查报道被主流财经媒体、法律博主引用转发，成为吃瓜界的"风向标"。
                </p>
                <p>
                  <strong className="text-foreground-950">投稿须知：</strong>
                  我们欢迎每一位掌握线索的网友前来投稿。爆料材料可包含文字、图片、音频、视频等多种形式。经核实发布的独家爆料，最高奖励5000元。所有爆料者身份严格保密，7×24小时接受投稿。
                </p>
              </div>

              <Link
                to="mailto:hello@epucz.cn"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all"
              >
                联系我们
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ SECTION ===== */}
      <section className="bg-background-50 py-12 md:py-20">
        <div className="px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              常见问题
            </h2>
            <p className="text-foreground-500 mt-2 text-sm md:text-base">
              关于91吃瓜网，您可能想了解这些
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {aboutFaq.map((item, idx) => (
              <div
                key={idx}
                className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/50"
              >
                <h3 className="font-semibold text-foreground-950 text-base md:text-lg mb-3">
                  Q：{item.question}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  A：{item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS GRID ===== */}
      <section className="bg-background-100 py-12 md:py-20">
        <div className="px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 md:mb-12">
            数据说话
            <span className="text-primary-500 ml-2">真实有力</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {siteStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 text-center"
              >
                <div className="h-[120px] md:h-[140px] flex items-center justify-center mb-4">
                  <span className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-primary-500">
                    {stat.value}
                  </span>
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-1">
                  {stat.label}
                </h3>
                <p className="text-sm text-foreground-500">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOT TAGS ===== */}
      <section className="bg-background-50 py-10 md:py-16">
        <div className="px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                热门吃瓜
                <span className="text-primary-500 ml-2">追踪</span>
              </h2>
              <p className="text-foreground-500 text-sm mt-2">全网热议话题实时统计</p>
            </div>
            <Link
              to="/category/social"
              className="hidden md:inline-flex items-center px-4 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-medium"
            >
              更多
            </Link>
          </div>

          <div className="flex flex-wrap gap-3">
            {hotTags.map((tag) => (
              <Link
                key={tag.name}
                to="/category/social"
                className="px-4 py-2.5 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all"
              >
                {tag.name}
                <span className="text-foreground-300 ml-1.5 text-xs">{tag.count}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT CTA ===== */}
      <section className="bg-foreground-950 py-12 md:py-20">
        <div className="px-4 md:px-8 lg:px-12 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-4">
            有料？联系我们
          </h2>
          <p className="text-background-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8">
            商务合作、爆料投稿、媒体采访、侵权投诉——无论您有任何需求，都可以通过以下方式联系91吃瓜网团队。工作日12小时内回复。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:hello@epucz.cn"
              className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              商务合作邮箱
            </a>
            <a
              href="mailto:hello@epucz.cn"
              className="px-6 py-3 rounded-full bg-background-800 text-background-50 text-sm font-semibold hover:bg-background-700 transition-colors whitespace-nowrap"
            >
              爆料投稿邮箱
            </a>
          </div>
          <p className="text-background-500 text-xs mt-6">
            客服咨询：hello@epucz.cn | 微信公众号：91chigua_official
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}